import { isNotEmpty, useForm } from '@mantine/form';
import { useTranslation } from 'i18n';
import { MENU_OBJECT_TYPES } from 'constants/menuManagement';
import {
  validateMinimum,
  validateMaximum,
  validateIndividualMinimum,
  validateIndividualMaximum,
  validateClassifications,
  validatePrice,
  validateServingsTag,
} from 'components/MenuManagement/validation';
import {
  AddNewDrawerFormValues,
  UseAddNewDrawerFormProps,
} from 'components/MenuManagement/MenuItemActions/types/newDrawerTypes';

export function useAddNewDrawerForm({
  itemType,
  parentRecord,
  newItemDefaults,
  isCateringMenu,
}: UseAddNewDrawerFormProps) {
  const { t } = useTranslation('menuManagementAddNew');
  const isItemOrModifier = [MENU_OBJECT_TYPES.items, MENU_OBJECT_TYPES.modifiers].includes(
    newItemDefaults.itemType,
  );

  const initialValues: AddNewDrawerFormValues = {
    name: '',
    classifications: [],
    description: '',
    parent: parentRecord?.name ?? '',
    minimum: '',
    maximum: '',
    multipleModifiersEnabled: false,
    individualMinimum: null,
    individualMaximum: null,
    price: null,
    servingsTag: null,
    ...(newItemDefaults.itemType === MENU_OBJECT_TYPES.modifiers && {
      isDefault: 'false',
    }),
  };

  const form = useForm<AddNewDrawerFormValues>({
    name: 'addItemDrawerForm',
    initialValues,
    validate: {
      name: isNotEmpty(t('useAddNewDrawerForm.nameRequired')),
      ...(itemType === MENU_OBJECT_TYPES.modifierGroups && {
        minimum: validateMinimum,
        maximum: validateMaximum,
        individualMinimum: (value, values) => {
          if (!values.multipleModifiersEnabled) return null;
          return validateIndividualMinimum(value, values);
        },
        individualMaximum: (value, values) => {
          if (!values.multipleModifiersEnabled) return null;
          return validateIndividualMaximum(value, values);
        },
      }),
      ...(isItemOrModifier && {
        classifications: validateClassifications,
        price: validatePrice,
      }),
      ...(newItemDefaults.itemType === MENU_OBJECT_TYPES.items &&
        isCateringMenu && {
          servingsTag: validateServingsTag,
        }),
    },
  });

  return {
    form,
    newItemHasClassifications: isItemOrModifier,
    pricesRequired: isItemOrModifier,
  };
}
